/**
 * INEQUALITIES.HELP
 * Help of the page "Disparités" : how to read it and how the dispersion of the answers is calculated
 */

import { WithTranslation, withTranslation } from "react-i18next"
import { INEQUALITY_COLORS } from "@/widgets/dashboard-inequalities-display.widget"
import {
  DISPARITY_HIGH,
  DISPARITY_LOW,
  DISPARITY_MEDIUM,
  DISPARITY_VERY_LOW,
  getDisparityLevel
} from "@/utils/get-disparity-level.utils"

type Props = WithTranslation

//Scores of the answers (0 to 100) : "strongly disagree" 0, "disagree" 25, "neutral" 50, "agree" 75, "strongly agree" 100
const TEAM_CLOSE: number[] = [50, 60, 60, 65, 65, 65, 70, 70, 75, 70]
const TEAM_SPLIT: number[] = [25, 25, 25, 25, 25, 100, 100, 100, 100, 100]

//Same calculation as the reporting : mean and standard deviation of the scores, rounded
function getMean(scores: number[]): number {
  return scores.reduce((sum, score) => sum + score, 0) / scores.length
}

function getStandardDeviation(scores: number[]): number {
  const mean = getMean(scores)
  return Math.round(Math.sqrt(scores.reduce((sum, score) => sum + (score - mean) ** 2, 0) / scores.length))
}

//Ranges of the levels (standard deviation on 100)
const LEVEL_RANGES: string[] = [
  "",
  `moins de ${DISPARITY_VERY_LOW}`,
  `de ${DISPARITY_VERY_LOW} à moins de ${DISPARITY_LOW}`,
  `de ${DISPARITY_LOW} à moins de ${DISPARITY_MEDIUM}`,
  `de ${DISPARITY_MEDIUM} à moins de ${DISPARITY_HIGH}`,
  `${DISPARITY_HIGH} et plus`
]

function InequalitiesHelp(props: Props) {
  const { t } = props

  const examples = [
    { name: "Équipe A (10 personnes, avis proches)", scores: TEAM_CLOSE },
    { name: "Équipe B (10 personnes, avis opposés)", scores: TEAM_SPLIT }
  ].map((team) => {
    const sd = getStandardDeviation(team.scores)
    return { ...team, mean: getMean(team.scores) / 10, sd, level: getDisparityLevel(sd) }
  })

  return (
    <div>
      <p>
        Cette page, réservée à l&apos;équipe IDTree, repère les questions sur lesquelles les avis <b>divergent</b> au
        sein d&apos;une même population. Une moyenne peut cacher un désaccord : si 5 personnes répondent « pas
        d&apos;accord » et 5 « tout à fait d&apos;accord », la moyenne est correcte alors que l&apos;équipe est coupée
        en deux. La dispersion rend ce désaccord visible.
      </p>

      <h3>Comment lire la page</h3>
      <ul>
        <li>
          <b>« Filtrer les résultats »</b> (barre du haut) : restreint la page à une partie des répondants, par exemple
          un seul service. C&apos;est ce qui permet de « descendre » dans l&apos;organisation.
        </li>
        <li>
          <b>« Découper par »</b> : choisit les populations que l&apos;on compare (service, site, contrat, genre, âge,
          ancienneté…). Exemple : restreindre à un service, puis découper par équipe.
        </li>
        <li>
          <b>Une ligne par question à échelle</b> (les autres types de question n&apos;apparaissent pas). La première
          ligne regroupe toutes les questions.
        </li>
        <li>
          <b>Le rond à gauche</b> : le niveau de dispersion des réponses de <i>tous</i> les répondants à cette question.
          Un clic affiche la répartition des réponses.
        </li>
        <li>
          <b>Les pastilles à droite</b> : les populations du découpage où les avis divergent le plus, de la plus à la
          moins dispersée (trois au maximum, « voir tout » pour les autres). Un clic affiche la répartition des réponses
          de cette population.
        </li>
      </ul>

      <h3>Les niveaux</h3>
      <table>
        <thead>
          <tr>
            <th>{t("utils_level")}</th>
            <th>{t("color")}</th>
            <th>Dispersion</th>
            <th>Niveau</th>
          </tr>
        </thead>
        <tbody>
          {INEQUALITY_COLORS.map((color: string, index: number) => (
            <tr key={index}>
              <td>{index}</td>
              <td>
                <div
                  style={{
                    backgroundColor: color,
                    border: "1px solid #e9e9e9",
                    borderRadius: 30,
                    height: 30,
                    width: 30
                  }}
                />
              </td>
              <td>{index === 0 ? "—" : LEVEL_RANGES[index]}</td>
              <td>{t("disparity_level_" + index)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <p>
        Une pastille de population n&apos;apparaît qu&apos;à partir de <b>{DISPARITY_VERY_LOW}</b> : en dessous, les
        avis sont proches et il n&apos;y a rien à regarder. Ces seuils sont provisoires, à ajuster avec l&apos;usage.
      </p>

      <h3>Comment c&apos;est calculé</h3>
      <ol>
        <li>
          Chaque réponse est convertie en <b>note de 0 à 100</b> (sur 5 réponses : 0, 25, 50, 75, 100 ; inversée pour
          une question inversée). Les personnes qui ne se prononcent pas ne sont pas comptées.
        </li>
        <li>
          Pour chaque population, on calcule l&apos;<b>écart-type</b> de ces notes : plus il est grand, plus les avis
          sont éloignés les uns des autres. Il vaut 0 si tout le monde répond pareil, et 50 au maximum (la moitié à 0,
          l&apos;autre moitié à 100).
        </li>
        <li>
          La population n&apos;est calculée que si elle compte au moins le <b>minimum de participation du compte</b> (5
          réponses par défaut), pour ne pas exposer des personnes.
        </li>
      </ol>

      <h3>Exemple</h3>
      <p>Deux équipes de 10 personnes répondent à la même question (« tout à fait d&apos;accord » = 10) :</p>
      <table>
        <thead>
          <tr>
            <th>Équipe</th>
            <th>Réponses</th>
            <th>Note moyenne</th>
            <th>Dispersion</th>
            <th>Niveau</th>
          </tr>
        </thead>
        <tbody>
          {examples.map((team) => (
            <tr key={team.name}>
              <td>{team.name}</td>
              <td>{team.scores.map((score) => score / 10).join(" ")}</td>
              <td>{team.mean.toFixed(1)} / 10</td>
              <td>{team.sd}</td>
              <td>{t("disparity_level_" + team.level)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p>
        Les deux équipes ont presque la même moyenne, mais la deuxième est partagée entre deux camps : c&apos;est elle
        qu&apos;il faut aller écouter. Un désaccord aussi net n&apos;atteint que le niveau « modéré » : les seuils sont
        à régler en conséquence.
      </p>
    </div>
  )
}

export default withTranslation()(InequalitiesHelp)
